// Exit-intent popup with 15€ discount code. Triggered on desktop mouseleave-top.
import { useEffect, useState } from "react";
import { X, Gift, Copy, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";

const LS_KEY = "talkani_exit_intent_seen_v1";
const DISCOUNT_CODE = "TALKANI15";
const DISCOUNT_EUR = 15;

export default function ExitIntentPopup() {
  const { t } = useLang();
  const ct = t.exit_intent;

  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(LS_KEY) === "1") return;
    // Don't show on mobile (no mouseleave-top trigger)
    if (window.matchMedia("(max-width: 768px)").matches) return;

    let armed = false;
    // Arm only after user has been on the page for 8 seconds
    const armTimer = setTimeout(() => {
      armed = true;
    }, 8000);

    const handleMouseLeave = (e) => {
      // Only trigger when leaving from the top of the viewport
      if (!armed) return;
      if (e.clientY <= 0) {
        setOpen(true);
        localStorage.setItem(LS_KEY, "1");
        window.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
    window.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      clearTimeout(armTimer);
      window.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(DISCOUNT_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[ExitIntent] clipboard write failed:", err);
      }
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-[fadeIn_.3s_ease-out]"
      data-testid="exit-intent-popup"
      onClick={() => setOpen(false)}
    >
      <div
        className="relative max-w-md w-full bg-white dark:bg-[#0F1012] shadow-2xl overflow-hidden animate-[scaleIn_.4s_cubic-bezier(.34,1.56,.64,1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          className="absolute top-3 end-3 z-10 w-9 h-9 flex items-center justify-center text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition"
          data-testid="exit-intent-close"
        >
          <X size={20} />
        </button>

        <div className="bg-gradient-to-br from-[#FF5A00] via-[#FF7A30] to-[#FF9558] text-white p-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm mb-4">
            <Gift size={26} />
          </div>
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase opacity-80 mb-2">
            {ct.eyebrow}
          </p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter leading-tight mb-2">
            {ct.title}
          </h2>
          <p className="text-sm opacity-95 leading-relaxed">{ct.subtitle}</p>
        </div>

        <div className="p-7">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-neutral-500 text-center mb-3">
            {ct.your_code}
          </p>
          <button
            type="button"
            onClick={copyCode}
            className="w-full flex items-center justify-between gap-3 border-2 border-dashed border-[#FF5A00] bg-[#FFF4ED] dark:bg-[#1A0F08] px-5 py-4 hover:bg-[#FFE9D6] dark:hover:bg-[#2A1A10] transition group"
            data-testid="exit-intent-copy-code"
          >
            <span className="font-black text-2xl tracking-widest text-[#FF5A00]">{DISCOUNT_CODE}</span>
            <span className="flex items-center gap-1.5 text-xs font-bold tracking-wide text-neutral-700 dark:text-neutral-300">
              {copied ? (
                <>
                  <Check size={14} className="text-emerald-600" /> {ct.copied}
                </>
              ) : (
                <>
                  <Copy size={14} /> {ct.copy}
                </>
              )}
            </span>
          </button>

          <Link
            to="/book"
            onClick={() => setOpen(false)}
            className="block mt-5 bg-[#090A0B] hover:bg-[#FF5A00] text-white text-center font-bold tracking-wider uppercase text-sm py-4 transition-colors"
            data-testid="exit-intent-book-btn"
          >
            {ct.cta}
          </Link>
          <p className="text-[10px] text-neutral-400 text-center mt-3 leading-relaxed">
            {ct.note} · {DISCOUNT_EUR}€ {ct.discount_label}
          </p>
        </div>
      </div>
    </div>
  );
}
